import React from 'react'
import { Link } from 'react-router-dom'
import tw from 'twin.macro'
import styled from 'styled-components'
import { SlButton } from '@shoelace-style/shoelace/dist/react'
import { SerializedToken } from '@nominex/nomiswap-sdk'
import { Image, NmxIcon } from '@nominex/dex-uikit'
import AddToWalletButton from 'components/AddToWallet/AddToWalletButton'
import LoadableValue from 'components/LoadableValue'
import { NetworkLogo } from 'components/NetworkLogo'
import { NMX } from 'config/tokens'
import { useTranslation } from 'contexts/Localization'
import { useWagmi } from 'libs/wagmi'
import { useNmxUsdtPrice } from 'hooks/useUsdtPrice'
import { formatNumber } from 'utils/formatters'
import { useExchangeInfo } from 'hooks/useExchangeInfo'

const StyledWrapper = styled.div`
  background: linear-gradient(180deg, #24365f4c 0%, #1a27454c 100%);
  ${tw`rounded-8`}
`

export const TvlWidget: React.FC = () => {
  const { t } = useTranslation()
  const { chainId } = useWagmi()
  const { exchangeInfo } = useExchangeInfo()

  const nmxPrice = Number((useNmxUsdtPrice() || 0).toFixed(8))
  const tvlInUsd = exchangeInfo.tvlInUsdByChain?.[chainId] || exchangeInfo.tvlInUsd

  return (
    <StyledWrapper className="text-12 text-dark-gray-100 pt-20 px-12 pb-12 lg:max-w-[450px] w-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-8">
          <NetworkLogo chainId={chainId} />
          <div className="flex flex-col gap-4">
            <span className="leading-none">{t('Total value locked')}</span>
            <LoadableValue loading={!Number(tvlInUsd)} fontSize="16px" width="106px" height="16px" bold>
              {formatNumber(Number(tvlInUsd), 'toIntUSD')}
            </LoadableValue>
          </div>
        </div>
        <a
          href="https://www.certik.com/projects/nomiswap"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-dark-gray-900 bg-opacity-50 rounded-8 px-12 py-4"
        >
          <Image src="/images/audits/certik.png" alt="Audited by Certik" width={105} height={28} />
        </a>
      </div>

      <StyledWrapper className="grid lg:grid-flow-col gap-16 p-20 mt-20">
        <div className="flex flex-wrap items-center justify-between gap-12 w-full self-start">
          <div className="flex space-x-8 notranslate" translate="no">
            <NmxIcon width="32px" />
            <div className="flex flex-col gap-4">
              <span className="leading-none">NMX</span>
              <LoadableValue loading={!nmxPrice} fontSize="16px" width="47px" height="16px" bold>
                {formatNumber(nmxPrice, 'toUSD')}
              </LoadableValue>
            </div>
          </div>

          {NMX[chainId] && (
            <div className="text-16 flex items-center gap-16">
              <Link to={`/swap?outputCurrency=${NMX[chainId].address}`}>
                <SlButton variant="primary" size="small">
                  {t('Buy NMX')}
                </SlButton>
              </Link>
              <AddToWalletButton token={NMX[chainId] as SerializedToken} />
            </div>
          )}
        </div>

        <div className="space-y-8 w-full whitespace-nowrap">
          <div className="flex justify-between gap-12">
            <span>{t('Max supply')}</span>
            <span className="text-white font-semibold">{formatNumber(Number(exchangeInfo.maxSupply))} NMX</span>
          </div>
          <div className="flex justify-between gap-12">
            <span>{t('Total supply')}</span>
            <span className="text-white font-semibold">{formatNumber(Number(exchangeInfo.totalSupply))} NMX</span>
          </div>
          <div className="flex justify-between gap-12">
            <span>{t('Circulating supply')}</span>
            <span className="text-white font-semibold">{formatNumber(Number(exchangeInfo.circulatingSupply))} NMX</span>
          </div>
          <div className="flex justify-between gap-12">
            <span>{t('Total burned')}</span>
            <span className="text-white font-semibold">{formatNumber(Number(exchangeInfo.totalBurned))} NMX</span>
          </div>
          <div className="flex justify-between gap-12">
            <span>{t('Market cap')}</span>
            <span className="text-white font-semibold">{formatNumber(Number(exchangeInfo.marketCap), 'toIntUSD')}</span>
          </div>
        </div>
      </StyledWrapper>
    </StyledWrapper>
  )
}
